package com.iregained.site.components.sections.resources.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.resources.styles.*
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

// UNIQUE ANIMATION: Staggered Float Up
@Composable
private fun Modifier.staggeredFloatUp(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateY(if (isVisible) 0.px else 30.px).transition(
        Transition.of("opacity", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms),
        Transition.of("transform", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms)
    )
}

@Composable
fun ResourcesGridSection(data: ResourcesGridData = defaultResourcesGridData) {
    val breakpoint = com.iregained.site.utils.LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "resources-evidence"

    var selectedCategory by remember { mutableStateOf(ResourceCategory.ALL) }
    var isVisible by remember { mutableStateOf(false) }
    val palette = ColorMode.current.toSitePalette()

    val filteredResources = if (selectedCategory == ResourceCategory.ALL) {
        data.items
    } else {
        data.items.filter { it.category == selectedCategory }
    }

    val groupedResources = filteredResources.groupBy { it.year }.entries.sortedByDescending { it.key }

    Column(
        modifier = ResourcesGridContainerStyle.toModifier().id(targetId).backgroundColor(palette.custom.lightGray)
        .styleModifier { property("scroll-margin-top", "100px") }
        .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent),
        horizontalAlignment = Alignment.CenterHorizontally) {
        if (data.items.isNotEmpty()) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.Center)
                    .toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element); onDispose { observer.disconnect() }
                        }
                    }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1440.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    // FILTER BAR
                    Row(
                        modifier = ResourceFilterWrapperStyle.toModifier().margin(bottom = 40.px)
                            .opacity(if (isVisible) 1 else 0).transition(Transition.of("opacity", 0.8.s))
                    ) {
                        ResourceCategory.entries.forEach { category ->
                            key(category.name) {
                                val isActive = selectedCategory == category
                                val buttonStyle = if (isActive) ResourceFilterButtonStyle.toModifier()
                                    .then(ResourceFilterButtonActiveStyle.toModifier()) else ResourceFilterButtonStyle.toModifier()
                                Button(attrs = buttonStyle.onClick { selectedCategory = category }.toAttrs()) {
                                    Text(
                                        category.displayName
                                    )
                                }
                            }
                        }
                    }

                    // RESOURCE GRID (Grouped by Year)
                    groupedResources.forEach { (year, items) ->
                        key(year) {
                            Column(modifier = Modifier.fillMaxWidth().margin(bottom = 50.px)) {
                                SpanText(
                                    text = year.toString(),
                                    modifier = Modifier.fontSize(24.px).fontWeight(FontWeight.Bold)
                                        .margin(bottom = 20.px).color(ColorMode.current.toSitePalette().brand.primary)
                                        .opacity(if (isVisible) 1 else 0).transition(Transition.of("opacity", 0.8.s))
                                )
                                SimpleGrid(
                                    numColumns(base = 1, lg = 2), modifier = ResourceCardGridStyle.toModifier()
                                ) {
                                    items.forEachIndexed { index, item ->
                                        key(item.plainEnglishTitle) {
                                            // Stagger cards within their respective year blocks
                                            ResourceCard(
                                                item, Modifier.staggeredFloatUp(isVisible, 150 + (index * 100))
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ResourceCard(item: ResourceItemData, modifier: Modifier = Modifier) {
    Link(path = item.downloadUrl, modifier = Modifier.textDecorationLine(TextDecorationLine.None).then(modifier)) {
        val palette = ColorMode.current.toSitePalette()
        Box(modifier = ResourceCardStyle.toModifier()) {
            Column(modifier = Modifier.fillMaxSize(), verticalArrangement = Arrangement.SpaceBetween) {
                Column {
                    if (item.fileTypeBadge.isNotBlank()) Row(
                        modifier = ResourceCardHeaderStyle.toModifier().margin(bottom = 12.px)
                    ) { SpanText(text = item.fileTypeBadge, modifier = ResourceBadgeStyle.toModifier()) }
                    if (item.plainEnglishTitle.isNotBlank()) SpanText(
                        text = item.plainEnglishTitle,
                        modifier = ResourcePlainTitleStyle.toModifier().margin(bottom = 8.px)
                    )
                    if (item.academicTitle.isNotBlank()) Row(
                        modifier = Modifier.margin(bottom = 16.px), verticalAlignment = Alignment.Top
                    ) {
                        Image(
                            src = Res.Images.Resources.Grid.ACADEMIC_TITLE,
                            alt = "Academic Title Icon",
                            modifier = Modifier.size(24.px).margin(right = 10.px, top = 5.px)
                        ); SpanText(
                        text = item.academicTitle,
                        modifier = ResourceAcademicTitleStyle.toModifier().color(palette.custom.darkText)
                            .fontSize(15.px).lineHeight(1.5)
                    )
                    }
                }
                Column(modifier = Modifier.margin(bottom = 20.px)) {
                    if (item.authors.isNotBlank()) {
                        Row(
                            modifier = Modifier.margin(bottom = 6.px), verticalAlignment = Alignment.CenterVertically
                        ) {
                            Image(
                                src = Res.Images.Resources.Grid.AUTHOR,
                                alt = "Authors Icon",
                                modifier = Modifier.size(24.px).margin(right = 10.px, top = 0.px)
                            ); SpanText(
                            text = item.authors, modifier = Modifier.fontSize(14.px).color(palette.custom.darkText)
                        )
                        }
                        if (item.venue.isNotBlank()) Row(verticalAlignment = Alignment.CenterVertically) {
                            Image(
                                src = Res.Images.Resources.Grid.VENUE,
                                alt = "University Icon",
                                modifier = Modifier.size(24.px).margin(right = 10.px, top = 0.px)
                            ); SpanText(
                            text = item.venue,
                            modifier = Modifier.fontSize(14.px).fontWeight(FontWeight.Medium)
                                .color(palette.custom.darkText)
                        )
                        }
                    }
                }
                Row(
                    modifier = ResourceCardFooterStyle.toModifier().fillMaxWidth(),
                    horizontalArrangement = Arrangement.End
                ) { Box(modifier = ResourceActionIconStyle.toModifier()) { Text("↗") } }
            }
        }
    }
}

// ==========================================
// CMS DATA MODEL & ARCHITECTURE
// ==========================================
enum class ResourceCategory(val displayName: String) {
    ALL("All Resources"), PRESENTATIONS("Scientific Presentations"), CLINICAL("Clinical Studies"), HARDWARE("Hardware Specs"), SOFTWARE(
        "Software & IT"
    ),
    PATIENT("Patient Guides")
}

data class ResourceItemData(
    val category: ResourceCategory,
    val fileTypeBadge: String,
    val plainEnglishTitle: String,
    val academicTitle: String = "",
    val authors: String,
    val venue: String,
    val year: Int,
    val downloadUrl: String
)

data class ResourcesGridData(
    val sectionId: String? = null, val items: List<ResourceItemData> = listOf(
        // 2025
        ResourceItemData(
            category = ResourceCategory.PRESENTATIONS,
            fileTypeBadge = "Conference Presentation",
            plainEnglishTitle = "Post-Stroke Sensorimotor Functioning Outcomes",
            academicTitle = "Change in Hand Sensorimotor Functioning Post-Stroke Resulting from Hand Function Therapy",
            authors = "Shima, M., Marshall, M., Dumais, E., Subramanian, S., Dorman, S., Johnson, V., Li, C.",
            venue = "American Physical Therapy Association-Combined Sections Meeting, Houston",
            year = 2025,
            downloadUrl = "#"
        ),
        // 2024
        ResourceItemData(
            category = ResourceCategory.PRESENTATIONS,
            fileTypeBadge = "Conference Presentation",
            plainEnglishTitle = "Gamification Preferences in Neurorehabilitation",
            academicTitle = "Cognitive Load and Game Preferences in the Gamification of Neurorehabilitation-Centric Hand Exercises in Chronic Stroke",
            authors = "Doan, A., Dumais, E., Beauchamp, S., Prefetto, S., Shima, M., Dorman, S., Singh, R.J., Johnson, V.B.K., Grewal, R.",
            venue = "World Congress on Virtual Rehabilitation, Amsterdam",
            year = 2024,
            downloadUrl = "#"
        ), ResourceItemData(
            category = ResourceCategory.PRESENTATIONS,
            fileTypeBadge = "Conference Presentation",
            plainEnglishTitle = "Efficacy of Novel Hand Training Device in Chronic Stroke",
            academicTitle = "The impact of hand function training with a novel device in a sample of chronic stroke patients",
            authors = "Nelson, A., Rehsi, R., Shima, M., Dumais, E., Rogojin, A., Meltzer, J., Johnson, V., Harris, J., Marshall, M.",
            venue = "World Congress for NeuroRehabilitation, Vancouver",
            year = 2024,
            downloadUrl = "#"
        ),
        // 2023
        ResourceItemData(
            category = ResourceCategory.PRESENTATIONS,
            fileTypeBadge = "Conference Presentation",
            plainEnglishTitle = "Neurorehabilitation of the Hand Overview",
            academicTitle = "Neurorehabilitation of the Hand in Chronic Stroke",
            authors = "Doan, A., Cayen, K., Marshall, M., Pilon, S., Dumais, E., Singh, RJ., Johnson, VBK., Grewal, R.",
            venue = "Advances in Stroke Recovery Conference, Toronto",
            year = 2023,
            downloadUrl = "#"
        ), ResourceItemData(
            category = ResourceCategory.PRESENTATIONS,
            fileTypeBadge = "Conference Presentation",
            plainEnglishTitle = "3-Week Passive Device Training Outcomes",
            academicTitle = "Changes in Hand Function After Three-weeks of Training Using a Novel Passive Device",
            authors = "Meltzer, JA., de Grosbois, J., Marshall, M., Dumais, E., Alibhai-Najarali, S., Wang, G., Heleno, M., Pan, S., Arya, A., Shao, J., Nelson, A., Johnson, VBK., Harris, JE.",
            venue = "Advances in Stroke Recovery Conference, Toronto",
            year = 2023,
            downloadUrl = "#"
        ), ResourceItemData(
            category = ResourceCategory.PRESENTATIONS,
            fileTypeBadge = "Conference Presentation",
            plainEnglishTitle = "Neurophysiological Changes After 15 Hours of Training",
            academicTitle = "Changes in Neurophysiological and Upper Extremity Activity After 15-hours of Hand Function Training in Chronic Stroke",
            authors = "Meltzer, JA., de Grosbois, J., Marshall, M., Dumais, E., Alibhai-Najarali, S., Wang, G., Heleno, M., Pan, S., Arya, A., Shao, J., Nelson, A., Johnson, VBK., Harris, JE.",
            venue = "Rotman Research Institute Conference, Toronto",
            year = 2023,
            downloadUrl = "#"
        ), ResourceItemData(
            category = ResourceCategory.PRESENTATIONS,
            fileTypeBadge = "Conference Presentation",
            plainEnglishTitle = "Chronic Stroke Hand Function Recovery Using Passive Therapy",
            academicTitle = "Changes in Hand Function in Chronic Stroke After Using a Novel Passive Hand Function Therapy Device for 3-Weeks",
            authors = "Meltzer J., de Grosbois J., Marshall M., Dumais E., Alibhai-Najarali S., Wang G., Heleno M., Pan S.Y., Arya A., Shao J., Shima M., Rogojin A., Nelson A., Johnson V.B.K., Harris J.E.",
            venue = "RehabWeek, Singapore",
            year = 2023,
            downloadUrl = "#"
        ), ResourceItemData(
            category = ResourceCategory.PRESENTATIONS,
            fileTypeBadge = "Conference Presentation",
            plainEnglishTitle = "Impact of Novel Device Training in Hand Therapy",
            academicTitle = "The Impact of Training Hand Function with a Novel Device in a Sample of Chronic Stroke Patients",
            authors = "Meltzer, JA., de Grosbois, J., Dumais, E., Marshall, M., Wang, G., Alibhai-Najarali, S., Heleno, M., Pan, S., Arya, A., Shao, J., Nelson, A., Harris, JE., Johnson, VBK.",
            venue = "American Society of Hand Therapists Annual Meeting, San Antonio",
            year = 2023,
            downloadUrl = "#"
        ), ResourceItemData(
            category = ResourceCategory.PRESENTATIONS,
            fileTypeBadge = "Conference Presentation",
            plainEnglishTitle = "Cortical Changes Resulting from Passive Device Training",
            academicTitle = "Cortical and Functional Changes in Hand Function After 3-Weeks of Training Using a Novel Passive Device",
            authors = "Meltzer, JA., de Grosbois, J., Marshall, M., Dumais, E., Alibhai-Najarali, S., Wang, G., Heleno, M., Pan, S., Arya, A., Shao, J., Nelson, A., Johnson, VBK., Harris, JE.",
            venue = "American Society of Neurorehabilitation Annual Meeting, Charleston",
            year = 2023,
            downloadUrl = "#"
        ),
        // 2018
        ResourceItemData(
            category = ResourceCategory.PRESENTATIONS,
            fileTypeBadge = "Conference Presentation",
            plainEnglishTitle = "Muscle Pattern Activity Following Stroke Device Evaluation",
            academicTitle = "Evaluation of a novel hand training device and program on muscle pattern activity following stroke",
            authors = "Pepe, A., Vasiliu, D., Vollebregt, B., Jain, A., Reinikka, K., Lawrence-Dewar, J., Johnson, VBK.",
            venue = "World Stroke Congress, Montreal",
            year = 2018,
            downloadUrl = "#"
        ), ResourceItemData(
            category = ResourceCategory.PRESENTATIONS,
            fileTypeBadge = "Conference Presentation",
            plainEnglishTitle = "Qualitative Analysis of Community-Based Stroke Recovery",
            academicTitle = "The Impact of a Hand Training Programme in Chronic Stroke Survivors: A Qualitative Analysis of a Community Based Program",
            authors = "Vollebregt, B., Reinikka, K., Vasiliu, D., Pepe, AM., Jain, A., Lawrence-Dewar, JM., Johnson, VBK.",
            venue = "Northern Constellations Conference, Thunder Bay",
            year = 2018,
            downloadUrl = "#"
        ),
        // 2017
        ResourceItemData(
            category = ResourceCategory.PRESENTATIONS,
            fileTypeBadge = "Conference Presentation",
            plainEnglishTitle = "Functional Changes from 6-Week Device Training",
            academicTitle = "Functional Changes Secondary to A 6-Week Hand Training Program Using a Novel Concept Rehabilitation Device",
            authors = "Vollebregt, B., Vasiliu, D., Pepe, AM., Vaclav, M., Jain, A., Lawrence-Dewar, JM., Reinikka, K., Johnson, VBK.",
            venue = "American Society of Neuro Rehabilitation, Annual Meeting, Baltimore",
            year = 2017,
            downloadUrl = "#"
        ), ResourceItemData(
            category = ResourceCategory.PRESENTATIONS,
            fileTypeBadge = "Conference Presentation",
            plainEnglishTitle = "Subjective Feedback on 6-Week Rehabilitation Device Training",
            academicTitle = "Subjective Changes Secondary to A Six-Week Hand Training Program Using a Novel Concept Rehabilitation Device",
            authors = "Coccimiglio, G., Vasiliu, D., Pepe, AM., Jain, A., Lawrence-Dewar, JM., Reinikka, K., Johnson, VBK.",
            venue = "Northern Health Research Conference, Thunder Bay",
            year = 2017,
            downloadUrl = "#"
        )
    )
)

val defaultResourcesGridData = ResourcesGridData()